/**
 * PadRedoDialog
 *
 * Central, reusable dialog for the SLaM "redo Post-Assessment Discussion" flow
 * that fires when the finalised Diagnostic Decision (DD) disagrees with the
 * outcome the PAD was actually held under.
 *
 * It is launched from three places, so it is built as one cohesive component
 * rather than splicing multi-step state into several callers:
 *   1. At DD-module completion (3_Formulation.js) — mode="decision": the
 *      clinician first chooses "Redo" vs "Leave it as is". Picking "Leave it as
 *      is" records an acknowledgement (via onLeaveAsIs) and closes; picking
 *      "Redo" advances to the details step.
 *   2. From the Patient Overview PAD card and the Documents dashboard row —
 *      mode="redo": the clinician has already decided, so it opens straight on
 *      the details step.
 *
 * All user-facing copy is direction-aware (positive->negative vs
 * negative->positive) and free of technical jargon ("archive"/"regenerate" are
 * never shown — "saved"/"view original" instead).
 *
 * Robustness: the caller passes onBeforeRedo (to flush pending debounced writes
 * so they cannot clobber the reset) and onAfterRedo (to re-fetch the patient and
 * navigate). The confirm button is disabled while the redo is in flight to
 * prevent a double-submit (which would create two archives + a double reset).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { ExclamationIcon, RefreshIcon, ArrowLeftIcon } from '@heroicons/react/outline';
import { getPadStaleness } from '../utils/conditionChecks';
import { TriggerRedoPad } from '../utils/TriggerRedoPad';
import SelectableOptionCard from './SelectableOptionCard';

// Shared button styling for the decision-step footer. Mirrors CIFinishPathDialog
// (bg-violet-900 primary, bordered secondary) so the two "how do you want to
// proceed?" dialogs read as visually identical.
const DECIDE_BTN = {
  secondary:
    'inline-flex items-center gap-1 py-2 px-4 font-semibold text-sm border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap disabled:opacity-50',
  primary:
    'inline-flex items-center px-4 py-2 font-semibold text-sm rounded-md transition-all duration-300 bg-violet-900 text-white hover:bg-violet-800',
  primaryDisabled:
    'inline-flex items-center px-4 py-2 font-semibold text-sm rounded-md bg-gray-300 text-gray-500 cursor-not-allowed',
};

// Positive decisions surfaced in the "you've recorded X" copy.
const POSITIVE_LABELS = new Set(['Fully met', 'Partially met']);

/**
 * Human-readable phrase for the recorded decision, used in the decision-dialog
 * body. Falls back to a plain positive/negative phrase when the exact label
 * isn't available.
 */
function describeRecordedDecision(diagnosisDecision, toOutcome) {
  if (diagnosisDecision && POSITIVE_LABELS.has(diagnosisDecision)) return diagnosisDecision;
  if (diagnosisDecision === 'Not met') return 'Not met';
  return toOutcome === 'positive' ? 'a positive ADHD outcome' : 'a non-diagnosis outcome';
}

export default function PadRedoDialog({
  isOpen,
  onClose,
  patientId,
  patientDataModel,
  mode = 'decision',
  diagnosisDecision = '',
  onLeaveAsIs,
  onBeforeRedo,
  onAfterRedo,
}) {
  // Direction of the change drives every string below.
  const { direction, toOutcome } = useMemo(
    () => getPadStaleness(patientDataModel),
    [patientDataModel]
  );
  const isPositiveToNegative = direction === 'positiveToNegative';

  // 'decide' (only in decision mode) -> 'details' -> in-flight/'error'
  const [step, setStep] = useState(mode === 'decision' ? 'decide' : 'details');
  const [newPadDate, setNewPadDate] = useState('');
  const [dateError, setDateError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  // Decision-step selection ('redo' | 'leave' | null). The clinician selects a
  // card then presses a single primary CTA — nothing happens on the card click
  // itself, so a stray click can't commit them to redoing or leaving.
  const [decideChoice, setDecideChoice] = useState(null);

  // Reset internal state whenever the dialog (re)opens.
  useEffect(() => {
    if (isOpen) {
      setStep(mode === 'decision' ? 'decide' : 'details');
      setNewPadDate('');
      setDateError(null);
      setSubmitError(null);
      setIsProcessing(false);
      setIsAcknowledging(false);
      setDecideChoice(null);
    }
  }, [isOpen, mode]);

  const recordedDecision = describeRecordedDecision(diagnosisDecision, toOutcome);

  const handleLeaveAsIs = useCallback(async () => {
    if (isAcknowledging) return;
    setIsAcknowledging(true);
    try {
      await onLeaveAsIs?.();
      onClose?.();
    } catch (err) {
      console.error('Failed to acknowledge PAD mismatch:', err);
      setSubmitError('Something went wrong. Please try again.');
      setIsAcknowledging(false);
    }
  }, [isAcknowledging, onLeaveAsIs, onClose]);

  const handleConfirmRedo = useCallback(async () => {
    if (isProcessing) return;
    if (!newPadDate) {
      setDateError('Add the new appointment date to continue.');
      return;
    }
    setDateError(null);
    setSubmitError(null);
    setIsProcessing(true);

    try {
      // Flush any pending debounced writes first so they cannot land on top of
      // the reset the Cloud Function is about to perform.
      if (onBeforeRedo) await onBeforeRedo();

      const result = await TriggerRedoPad(patientId, newPadDate);

      if (!result.success) {
        setSubmitError(result.message || 'Could not start a new discussion. Please try again.');
        setIsProcessing(false);
        return;
      }

      // Parent re-fetches the patient and navigates to the PAD Review page.
      await onAfterRedo?.(result);
      onClose?.();
    } catch (err) {
      console.error('Failed to redo PAD:', err);
      setSubmitError('Could not start a new discussion. Please try again.');
      setIsProcessing(false);
    }
  }, [isProcessing, newPadDate, onBeforeRedo, patientId, onAfterRedo, onClose]);

  // ─── Decision step (Redo vs Leave as is) ────────────────────────────────
  const renderDecide = () => (
    <div className="text-left">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
          <ExclamationIcon className="w-7 h-7 text-amber-600" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <AlertDialog.Title className="text-xl font-bold text-gray-900">
            Your decision differs from the Post-Assessment Discussion
          </AlertDialog.Title>
          <AlertDialog.Description className="text-sm text-gray-600 mt-1">
            {isPositiveToNegative
              ? `You've recorded ${recordedDecision === 'Not met' ? 'Not met' : 'a non-diagnosis outcome'}, but the Post-Assessment Discussion was completed for a positive ADHD outcome.`
              : `You've recorded ${recordedDecision}, but the Post-Assessment Discussion was completed for a negative outcome.`}
          </AlertDialog.Description>
        </div>
      </div>

      <p className="text-sm font-medium text-gray-800 mb-3">How would you like to proceed?</p>

      <div
        className="space-y-3"
        role="radiogroup"
        aria-label="How would you like to proceed?"
      >
        <SelectableOptionCard
          selected={decideChoice === 'redo'}
          onSelect={() => setDecideChoice('redo')}
          disabled={isAcknowledging}
          title="Redo the Post-Assessment Discussion"
          description="Start a fresh discussion for the new outcome at a new appointment. Your original is saved and stays viewable."
        />
        <SelectableOptionCard
          selected={decideChoice === 'leave'}
          onSelect={() => setDecideChoice('leave')}
          disabled={isAcknowledging}
          title="Leave it as is"
          description="Keep the completed discussion, acknowledging it reflects a different outcome from your decision."
        />
      </div>

      {submitError && (
        <p className="mt-3 text-xs text-red-700">{submitError}</p>
      )}

      {/* Go back (closes, returning to where they came from) + single primary
          CTA whose label mirrors the selected option — same layout as the CI
          finish-path dialog. Nothing is committed until this CTA is pressed. */}
      <div className="flex justify-between items-center border-t border-gray-200 pt-4 mt-4">
        <button
          type="button"
          onClick={() => onClose?.()}
          disabled={isAcknowledging}
          className={DECIDE_BTN.secondary}
        >
          <ArrowLeftIcon className="w-4 h-4" aria-hidden="true" />
          Go back
        </button>
        <button
          type="button"
          onClick={() => {
            if (decideChoice === 'redo') setStep('details');
            else if (decideChoice === 'leave') handleLeaveAsIs();
          }}
          disabled={!decideChoice || isAcknowledging}
          className={
            !decideChoice || isAcknowledging
              ? DECIDE_BTN.primaryDisabled
              : DECIDE_BTN.primary
          }
        >
          {decideChoice === 'leave'
            ? isAcknowledging
              ? 'Saving…'
              : 'Leave as is'
            : 'Continue'}
        </button>
      </div>
    </div>
  );

  // ─── Details step (direction-aware; captures new appointment date) ───────
  const renderDetails = () => (
    <div className="text-left">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-full bg-violet-100 flex items-center justify-center">
          <RefreshIcon className="w-7 h-7 text-violet-600" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <AlertDialog.Title className="text-xl font-bold text-gray-900">
            Redo the Post-Assessment Discussion
          </AlertDialog.Title>
        </div>
      </div>

      <div className="mb-4 bg-violet-50 border border-violet-200 rounded-lg p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2">
          What happens next
        </p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700">
          {isPositiveToNegative ? (
            <>
              <li>You'll record a short review of the assessment for a non-diagnosis outcome.</li>
              <li>The previous goals, treatment options and recommendations won't carry over.</li>
              <li>You'll see the patient at a new appointment to share the revised outcome.</li>
            </>
          ) : (
            <>
              <li>You'll complete the full discussion — goals, treatment options, strategies and health checks.</li>
              <li>This needs a new appointment with the patient.</li>
              <li>Your original discussion stays saved for reference.</li>
            </>
          )}
        </ul>
      </div>

      <div className="mb-2">
        <label htmlFor="pad-redo-new-date" className="block text-sm font-medium text-gray-800">
          New appointment date
        </label>
        <p className="text-xs text-gray-500 mb-1.5">When will you next see the patient?</p>
        <input
          id="pad-redo-new-date"
          type="date"
          value={newPadDate}
          onChange={(e) => {
            setNewPadDate(e.target.value);
            if (e.target.value) setDateError(null);
          }}
          disabled={isProcessing}
          className="w-52 max-w-full p-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50"
        />
        {dateError && <p className="mt-1 text-xs text-red-700">{dateError}</p>}
      </div>

      {submitError && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-xs text-red-700">{submitError}</p>
        </div>
      )}

      <div className="flex justify-between items-center border-t border-gray-200 pt-4 mt-4">
        <button
          type="button"
          onClick={() => (mode === 'decision' ? setStep('decide') : onClose?.())}
          disabled={isProcessing}
          className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50"
        >
          <ArrowLeftIcon className="w-4 h-4" aria-hidden="true" />
          Back
        </button>
        <button
          type="button"
          onClick={handleConfirmRedo}
          disabled={isProcessing}
          className={`px-4 py-2 text-sm font-semibold text-white rounded-md transition-colors ${
            isProcessing ? 'bg-gray-400 cursor-not-allowed' : 'bg-violet-700 hover:bg-violet-800'
          }`}
        >
          {isProcessing ? 'Starting…' : 'Start new discussion'}
        </button>
      </div>
    </div>
  );

  return (
    <AlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isProcessing) onClose?.();
      }}
    >
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-50" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[92vw] max-h-[88vh] overflow-y-auto shadow-xl z-50">
          <VisuallyHidden.Root>
            <AlertDialog.Title>Redo the Post-Assessment Discussion</AlertDialog.Title>
          </VisuallyHidden.Root>
          <VisuallyHidden.Root>
            <AlertDialog.Description>
              Choose whether to redo the Post-Assessment Discussion for the updated diagnosis.
            </AlertDialog.Description>
          </VisuallyHidden.Root>
          {step === 'decide' ? renderDecide() : renderDetails()}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
